import styled from '@emotion/styled';
import { Icon, Link, Text, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type {
  FeaturedInsightsType,
  InsightsData,
  SurveyData,
} from '@hotjar/webapp/domains/activation/featured-insights/types';
import { CategoryName as CategoryNameEnum } from '@hotjar/webapp/domains/activation/featured-insights/types';
import { useFeaturedInsightsThumbnails } from '@hotjar/webapp/domains/activation/featured-insights';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { useCurrentSite } from '@hotjar/site';
import { FeaturedInsightSidebar } from './components/FeaturedInsightSidebar';
import { FeaturedInsightMain } from './components/FeaturedInsightMain';

const Container = styled.article`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  height: 388px;
  background-color: ${tokens.color.neutral_100};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: 4px;
  overflow: hidden;

  @media (max-width: 1280px) {
    min-width: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 50px;
  padding: 0 ${tokens.spacer.x2};
  border-bottom: 1px solid ${tokens.color.ui_border};
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  width: ${tokens.spacer.x1pt5};
  min-width: ${tokens.spacer.x1pt5};
  height: ${tokens.spacer.x1pt5};
  min-height: ${tokens.spacer.x1pt5};
  margin-right: 6px;
`;

const CategoryName = styled(Text)`
  text-transform: capitalize;
`;

const StyledCta = styled(Link)`
  color: ${tokens.color.link};
  text-decoration: none;
`;

const ContentContainer = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
`;

export type FeaturedInsightsSlideProps = {
  details: InsightsData;
  type: FeaturedInsightsType;
  onClick: (slideId: string) => void;
};

export const FeaturedInsightsSlide = ({
  details,
  type,
  onClick,
}: FeaturedInsightsSlideProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: featuredInsightsThumbnail, isLoading } =
    useFeaturedInsightsThumbnails(details?.thumbnails?.[0]?.url) || {};
  const { data: site } = useCurrentSite();

  const getInsightLink = (insightType: FeaturedInsightsType) => {
    switch (insightType) {
      case 'survey':
        return getRoute(SURVEYS.RESPONSES.path, {
          siteId: site?.id || '',
          surveyId: (details as SurveyData)?.surveyId || '',
        });
      case 'heatmap':
        return `/sites/${site?.id}/heatmap/view?url=${details.id}`;

      default:
        return details?.url;
    }
  };

  return (
    <Container>
      <Header>
        <TitleContainer>
          <StyledIcon icon={CategoryNameEnum[type]} color="text_default" />
          <CategoryName textType="caption">
            {t(
              `activation:space-overview.for-you.featured-insights.header.categories.${CategoryNameEnum[type]}`,
              { count: 1 },
            )}
          </CategoryName>
        </TitleContainer>
        <StyledCta
          href={getInsightLink(type)}
          kind="inline"
          onClick={() => onClick(CategoryNameEnum[type])}
          data-testid="go-to-cta"
          isExternal={type === 'recording'}
        >
          {t(
            `activation:space-overview.for-you.featured-insights.view-in.${
              type === 'feedback' || type === 'survey' ? 'response' : type
            }`,
          )}
        </StyledCta>
      </Header>
      <ContentContainer>
        <FeaturedInsightMain
          category={CategoryNameEnum[type]}
          data={details}
          onClick={() => onClick(CategoryNameEnum[type])}
          isLoading={isLoading || featuredInsightsThumbnail?.status === 'pending'}
          thumbnail={
            featuredInsightsThumbnail?.status === 'success'
              ? featuredInsightsThumbnail?.imageUrl
              : undefined
          }
        />
        <FeaturedInsightSidebar category={CategoryNameEnum[type]} data={details} />
      </ContentContainer>
    </Container>
  );
};
